export const DELIVERY_FORMAT_OPTIONS = Object.freeze([
  ['PDF_ONLY', 'PDF uniquement'],
  ['PDF_AND_SOURCES', 'PDF + fichiers sources'],
  ['SOURCES_ONLY', 'Fichiers sources uniquement'],
  ['OTHER', 'Autre'],
]);

export const PAYMENT_TRIGGER_OPTIONS = Object.freeze([
  ['DATE', 'À une date déterminée'],
  ['PHASE', 'À une phase du projet'],
  ['LDA_COUNT_DIFFUSED', 'Nombre de livrables LDA diffusés'],
  ['LDA_PERCENT_DIFFUSED', 'Pourcentage de livrables LDA diffusés'],
  ['VISA_PERCENT_RECEIVED', 'Pourcentage de VISA déposés'],
  ['VISA_ALL_RECEIVED', 'Tous les VISA déposés'],
]);

export const deliveryFormatLabel = (code, other = '') => {
  if (code === 'OTHER') return other || 'Autre format à préciser';
  return DELIVERY_FORMAT_OPTIONS.find(([value]) => value === code)?.[1] || 'PDF uniquement';
};

const normalizedPresentationText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/\s*\/\s*/g, '/')
  .replace(/\s+/g, ' ')
  .replace(/[.!?]+$/g, '');

export const isTechnicalPlanningImportLabel = (value) => [
  'importe depuis planning ao/dce',
  'charge issue du planning ao/dce',
].includes(normalizedPresentationText(value));

export const formatCodeLabel = (code, label, fallback = 'Ensemble') => {
  const normalizedCode = String(code || '').trim();
  const normalizedLabel = String(label || '').trim();
  if (!normalizedCode) return normalizedLabel || fallback;
  if (!normalizedLabel || normalizedPresentationText(normalizedCode) === normalizedPresentationText(normalizedLabel)) {
    return normalizedCode;
  }
  return `${normalizedCode} - ${normalizedLabel}`;
};

export const paymentScheduleComplementaryPercentageTotal = (schedule = []) => schedule
  .filter((item) => item.amountType === 'PERCENTAGE' && item.triggerType !== 'ORDER')
  .reduce((total, item) => total + Number(item.percentage || 0), 0);

export const clientPaymentScheduleItems = (schedule = []) => schedule
  .filter((item) => item.triggerType !== 'ORDER');

export const paymentSchedulePercentageTotal = (schedule = [], depositRate = 0) => (
  paymentScheduleComplementaryPercentageTotal(schedule)
  + Number(depositRate || 0)
);

export const paymentScheduleIsPercentageOnly = (schedule = [], depositRate = 0) => {
  const complementaryItems = clientPaymentScheduleItems(schedule);
  return complementaryItems.length === 0
    || complementaryItems.every((item) => item.amountType === 'PERCENTAGE');
};

const reindexPaymentSchedule = (schedule) => schedule.map((item, orderIndex) => ({
  ...item,
  orderIndex,
}));

export const newPaymentScheduleItem = (orderIndex = 0, id) => ({
  id: id || `payment-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  orderIndex,
  amountType: 'PERCENTAGE',
  percentage: 0,
  fixedAmount: null,
  triggerType: '',
  triggerDate: null,
  phaseClientKey: null,
  phaseLabelSnapshot: null,
  thresholdValue: null,
  scopeType: 'ALL',
  scopePhaseCode: null,
  scopeLotCode: null,
  label: '',
});

export const addPaymentScheduleItem = (schedule = [], id) => [
  ...reindexPaymentSchedule(schedule),
  newPaymentScheduleItem(schedule.length, id),
];

export const removePaymentScheduleItem = (schedule = [], index) => (
  reindexPaymentSchedule(schedule.filter((_item, itemIndex) => itemIndex !== index))
);

export const movePaymentScheduleItem = (schedule = [], index, delta) => {
  const target = index + delta;
  if (index < 0 || index >= schedule.length || target < 0 || target >= schedule.length) {
    return reindexPaymentSchedule(schedule);
  }
  const next = [...schedule];
  [next[index], next[target]] = [next[target], next[index]];
  return reindexPaymentSchedule(next);
};

export const quoteParameterText = (parameters = [], code) => {
  const parameter = parameters.find((item) => item.parameterCode === code);
  return String(parameter?.valueText || '').trim();
};

const formatNumber = (value) => Number(value || 0).toLocaleString('fr-FR', {
  maximumFractionDigits: 2,
});

const scopeText = (item) => {
  if (item.scopeType === 'PHASE' && item.scopePhaseCode) return ` de la phase ${item.scopePhaseCode}`;
  if (item.scopeType === 'LOT' && item.scopeLotCode) return ` du lot ${item.scopeLotCode}`;
  return '';
};

export const paymentTriggerLabel = (item = {}) => {
  switch (item.triggerType) {
    case 'ORDER': return 'à la commande';
    case 'DATE': {
      const date = item.triggerDate ? new Date(`${item.triggerDate}T12:00:00`) : null;
      return date && !Number.isNaN(date.getTime())
        ? `le ${new Intl.DateTimeFormat('fr-FR').format(date)}`
        : 'à la date indiquée';
    }
    case 'PHASE': return `à la fin de la phase ${item.phaseLabelSnapshot || 'sélectionnée'}`;
    case 'LDA_COUNT_DIFFUSED': return `lorsque ${formatNumber(item.thresholdValue)} livrables${scopeText(item)} sont diffusés`;
    case 'LDA_PERCENT_DIFFUSED': return `lorsque ${formatNumber(item.thresholdValue)} % des livrables${scopeText(item)} sont diffusés`;
    case 'VISA_PERCENT_RECEIVED': return `lorsque ${formatNumber(item.thresholdValue)} % des VISA sont déposés`;
    case 'VISA_ALL_RECEIVED': return 'lorsque tous les VISA sont déposés';
    default: return 'déclencheur à sélectionner';
  }
};

export const paymentAmountLabel = (item = {}, formatMoney = (value) => `${Number(value || 0).toFixed(2)} €`) => (
  item.amountType === 'FIXED_AMOUNT'
    ? formatMoney(item.fixedAmount)
    : `${formatNumber(item.percentage)} %`
);

export const formatPaymentScheduleItem = (
  item = {},
  { formatMoney = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` } = {}
) => {
  const amount = item.amountType === 'FIXED_AMOUNT'
    ? `${formatMoney(item.fixedAmount)} HT`
    : `${formatNumber(item.percentage)} % du montant HT`;
  const precision = String(item.label || '').trim();
  return `${amount} - ${paymentTriggerLabel(item)}${precision ? ` - ${precision}` : ''}`;
};
